import { useRef, useEffect, useId } from 'react';
import { Box, Typography } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useDarkTheme } from '../../utils/useDarkTheme';
import {
	getErrorMessageA11yProps,
	getInputErrorA11yProps,
} from '../../utils/fieldError.utils';
import { useTranslation } from '../../hooks/useTranslation';

export default function PinGroupedField({
	value,
	onChange,
	onComplete,
	error,
	helperText,
	primaryColor = '#3053D3',
	fontFamily,
	customDetails,
	autoFocus = true,
	focusKey,
	labelId,
	pinLength = 4,
}) {
	const PIN_LENGTH = Number(pinLength) || 4;
	const { t } = useTranslation();
	const inputsRef = useRef([]);
	const isDark = useDarkTheme(customDetails);
	const errorId = useId();
	const hasError = Boolean(error);
	const errorMessage =
		typeof helperText === 'string'
			? helperText
			: hasError && helperText
				? helperText
				: '';

	const focusFirstInput = () => {
		const firstInput = inputsRef.current[0];
		if (!firstInput) return;
		inputsRef.current.forEach((input, index) => {
			if (index !== 0 && input) {
				input.blur();
			}
		});
		firstInput.focus({ preventScroll: true });
	};

	useEffect(() => {
		if (!autoFocus) return undefined;
		let rafId2;
		const rafId1 = requestAnimationFrame(() => {
			rafId2 = requestAnimationFrame(() => {
				focusFirstInput();
			});
		});
		return () => {
			cancelAnimationFrame(rafId1);
			if (rafId2) cancelAnimationFrame(rafId2);
		};
	}, [autoFocus, focusKey]);

	const handleChange = (e, index) => {
		const val = e.target.value.replace(/\D/, '');
		if (!val) return;

		const pinArray = value
			.split('')
			.concat(Array(PIN_LENGTH).fill(''))
			.slice(0, PIN_LENGTH);
		const firstEmptyIndex = pinArray.findIndex((digit) => !digit);
		const targetIndex =
			firstEmptyIndex !== -1 && index > firstEmptyIndex
				? firstEmptyIndex
				: index;

		pinArray[targetIndex] = val[0];
		const nextValue = pinArray.join('');
		onChange(nextValue);

		if (targetIndex < PIN_LENGTH - 1) {
			inputsRef.current[targetIndex + 1]?.focus();
		} else if (nextValue.length === PIN_LENGTH) {
			e.target.blur();
			window.setTimeout(() => onComplete?.(nextValue), 0);
		}
	};

	const handleKeyDown = (e, index) => {
		if (['PageUp', 'PageDown', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
			e.preventDefault();
		}

		if (e.key === 'Backspace') {
			e.preventDefault();
			const pinArray = value.split('');

			if (pinArray[index]) {
				pinArray[index] = '';
				onChange(pinArray.join(''));
			} else if (index > 0) {
				inputsRef.current[index - 1]?.focus();
				pinArray[index - 1] = '';
				onChange(pinArray.join(''));
			}
		}
	};

	const handlePaste = (e) => {
		e.preventDefault();
		const pasted = e.clipboardData
			.getData('text')
			.replace(/\D/g, '')
			.slice(0, PIN_LENGTH);
		onChange(pasted);
		if (pasted.length === PIN_LENGTH) {
			e.target.blur();
			window.setTimeout(() => onComplete?.(pasted), 0);
		} else if (pasted.length > 0) {
			inputsRef.current[Math.min(pasted.length, PIN_LENGTH) - 1]?.focus();
		}
	};

	const idleBorder = isDark ? '#4A5568' : '#D0D7E2';
	const idleBg = isDark ? '#2D2D2D' : '#E8EDF3';
	const focusBg = isDark
		? `color-mix(in srgb, ${primaryColor} 20%, #2D2D2D)`
		: `color-mix(in srgb, ${primaryColor} 10%, #E8EDF3)`;

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				width: '100%',
			}}
		>
			<Box
				role="group"
				aria-labelledby={labelId || undefined}
				aria-label={labelId ? undefined : t('pinSetup.pinGroupLabel')}
				aria-describedby={hasError ? errorId : undefined}
				sx={{
					display: 'flex',
					gap: PIN_LENGTH > 4 ? { xs: '0.25rem', sm: '0.375rem' } : '0.75rem',
					maxWidth: '100%',
					justifyContent: 'center',
					alignItems: 'center',
				}}
			>
				{Array.from({ length: PIN_LENGTH }).map((_, idx) => {
					const isFilled = Boolean(value[idx]);
					const digitLabel = isFilled
						? t('pinSetup.pinDigitEntered', {
								current: idx + 1,
								total: PIN_LENGTH,
							})
						: t('pinSetup.pinDigit', { current: idx + 1, total: PIN_LENGTH });

					return (
						<Box
							key={idx}
							component="input"
							type="tel"
							inputMode="numeric"
							pattern="[0-9]*"
							maxLength={1}
							value={value[idx] ? '•' : ''}
							placeholder="•"
							onChange={(e) => handleChange(e, idx)}
							onKeyDown={(e) => handleKeyDown(e, idx)}
							onPaste={handlePaste}
							autoComplete="off"
							aria-label={digitLabel}
							{...getInputErrorA11yProps(hasError, errorId)}
							ref={(el) => {
								inputsRef.current[idx] = el;
							}}
							sx={{
								width:
									PIN_LENGTH > 4
										? { xs: '2.125rem', sm: '2.375rem' }
										: '3.5rem',
								height:
									PIN_LENGTH > 4
										? { xs: '2.625rem', sm: '2.875rem' }
										: '3.75rem',
								borderRadius: '10px',
								border: `1px solid ${idleBorder}`,
								fontSize: PIN_LENGTH > 4 ? '1.125rem' : '1.25rem',
								fontFamily,
								textAlign: 'center',
								color: isDark ? '#fff' : '#333',
								caretColor: isDark ? '#fff' : '#333',
								outline: 'none',
								boxSizing: 'border-box',
								boxShadow: isDark ? 'none' : '0 1px 2px rgba(16, 24, 40, 0.06)',
								'&::placeholder': {
									color: isDark ? '#9AA3A8' : '#8A94A6',
									opacity: 0.6,
									fontSize: '0.875rem',
									textAlign: 'center',
								},
								'&:focus': {
									borderColor: primaryColor,
									borderWidth: '2px',
									backgroundColor: focusBg,
									boxShadow: isDark
										? 'none'
										: `0 0 0 3px color-mix(in srgb, ${primaryColor} 18%, transparent)`,
								},
								...(hasError && {
									borderColor: 'error.main',
									borderWidth: '2px',
								}),
							}}
						/>
					);
				})}
			</Box>
			{hasError && errorMessage && (
				<Typography
					{...getErrorMessageA11yProps(errorId)}
					color="error"
					fontSize="0.75rem"
					sx={{
						padding: 0,
						mt: '0.5rem',
						lineHeight: 1.5,
						display: 'flex',
						alignItems: 'flex-start',
						gap: '0.25rem',
						maxWidth: '280px',
					}}
				>
					<ErrorOutlineIcon
						sx={{ fontSize: '0.875rem', mt: '0.1rem', flexShrink: 0 }}
						aria-hidden
					/>
					<span>{errorMessage}</span>
				</Typography>
			)}
		</Box>
	);
}
